import { useCallback } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useSWR, useSWRConfig } from 'modules/network/hooks/useSwr'

import { ContractRociPayment } from 'modules/contracts/contracts'
import type { Chains } from 'modules/blockChain/chains'

export const getLoansListSwrKey = (chainId: Chains, walletAddress: string) =>
  `loans-${chainId}-${walletAddress}`

export function useLoansListRevalidate() {
  const { mutate } = useSWRConfig()
  const { chainId, walletAddress } = useWeb3()
  return useCallback(
    () => walletAddress && mutate(getLoansListSwrKey(chainId, walletAddress)),
    [mutate, chainId, walletAddress],
  )
}

export function useLoansList() {
  const { chainId, walletAddress } = useWeb3()

  const rociPayment = ContractRociPayment.useContractRpc()

  const loans = useSWR(
    walletAddress ? getLoansListSwrKey(chainId, walletAddress) : null,
    async () => {
      if (!walletAddress) return

      const loansCount = await rociPayment.getNumberOfLoans(walletAddress)
      const requestsLoans = Array.from(Array(Number(loansCount)))
        .map((_, i) => i)
        .reverse()
        .map(async i => {
          const loanId = await rociPayment.loanIDs(walletAddress, i)
          const [loan, isCompleted] = await Promise.all([
            rociPayment.loanLookup(loanId),
            rociPayment.isComplete(loanId),
          ])

          return {
            loanId: loanId.toString(),
            isCompleted: isCompleted || loan.status === 4,
            loan,
          }
        })
      const res = await Promise.all(requestsLoans)

      const sorted = res.sort((a, b) => {
        const maturityA = Number(a.loan.maturityDate)
        const maturityB = Number(b.loan.maturityDate)

        return maturityA - maturityB
      })

      return sorted
    },
  )

  return loans
}
